import { useState } from "react";
import {
  Box, Typography, Paper, Grid,
  Button, Table, TableHead, TableRow,
  TableCell, TableBody, CircularProgress,
  Alert, Dialog, TextField, Collapse, IconButton, Chip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import api from "../service/api";
import { entryReportService } from "../service/EntryReportService";

const EntryReport = () => {

  // 🔹 STATES
  const [mode, setMode] = useState("monthly");
  const [year, setYear] = useState(new Date().getFullYear());
  const [date, setDate] = useState("");

  const [selectedMonth, setSelectedMonth] = useState(null);

  const [tableData, setTableData] = useState({});
  const [weekList, setWeekList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [openView, setOpenView] = useState(false);
  const [viewData, setViewData] = useState(null);

  // 🔹 Collapse state per location — default all open
  const [collapsed, setCollapsed] = useState({});

  const toggleLocation = (loc) =>
    setCollapsed((p) => ({ ...p, [loc]: !p[loc] }));

  // 🔹 COMMON GROUP FUNCTION
  const groupByLocation = (bikes = []) => {
    const grouped = {};
    bikes.forEach((b) => {
      if (!grouped[b.location]) grouped[b.location] = [];
      grouped[b.location].push(b);
    });
    // Reset collapse state — open all newly loaded locations
    setCollapsed({});
    return grouped;
  };

  // 🔹 MONTH CLICK
  const handleMonthClick = async (month) => {
    try {
      setLoading(true);
      setSelectedMonth(month);
      const res = await entryReportService.getMonthly(year, month);
      const bikes = [
        ...(res.data?.new?.bikes || []),
        ...(res.data?.exchange?.bikes || [])
      ];
      setTableData(groupByLocation(bikes));
      setWeekList([]);
    } catch {
      setError("Monthly data failed");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 DAILY
  const handleDaily = async () => {
    try {
      setLoading(true);
      const res = await entryReportService.getByDate(date);
      const bikes = [
        ...(res.data?.new?.bikes || []),
        ...(res.data?.exchange?.bikes || [])
      ];
      setTableData(groupByLocation(bikes));
      setWeekList([]);
    } catch {
      setError("Daily data failed");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 WEEKLY LOAD
  const handleWeekly = async () => {
    try {
      setLoading(true);
      const res = await entryReportService.getWeekly(year);
      setWeekList(res.data?.new || []);
      setTableData({});
    } catch {
      setError("Weekly data failed");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 WEEK CLICK
  const handleWeekClick = (week) => {
    setTableData(groupByLocation(week.bikes || []));
  };

  // 🔹 VIEW
  const handleView = async (id) => {
    const res = await api.get(`/specific-bike-details/${id}`);
    setViewData(res.data.data);
    setOpenView(true);
  };

  return (
    <Box p={{ xs: 1, md: 3 }}>

      <Typography variant="h5" fontWeight={700}>
        Entry Report
      </Typography>

      {/* 🔥 MODE SWITCH */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Button variant={mode === "daily"   ? "contained" : "outlined"} onClick={() => setMode("daily")}>Daily</Button>
          </Grid>
          <Grid item>
            <Button variant={mode === "weekly"  ? "contained" : "outlined"} onClick={() => setMode("weekly")}>Weekly</Button>
          </Grid>
          <Grid item>
            <Button variant={mode === "monthly" ? "contained" : "outlined"} onClick={() => setMode("monthly")}>Monthly</Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField size="small" label="Year" fullWidth value={year} onChange={(e) => setYear(e.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      {/* 🔥 DAILY UI */}
      {mode === "daily" && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField type="date" fullWidth value={date} onChange={(e) => setDate(e.target.value)} />
            </Grid>
            <Grid item>
              <Button variant="contained" onClick={handleDaily}>Load</Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* 🔥 WEEKLY UI */}
      {mode === "weekly" && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Button variant="contained" onClick={handleWeekly}>Load Weekly Data</Button>
          <Grid container spacing={1} mt={1}>
            {weekList.map((w) => (
              <Grid item key={w.week}>
                <Button variant="outlined" onClick={() => handleWeekClick(w)}>Week {w.week}</Button>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* 🔥 MONTHLY UI */}
      {mode === "monthly" && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Grid container spacing={1}>
            {Array.from({ length: 12 }, (_, i) => (
              <Grid item xs={4} sm={3} md={2} key={i}>
                <Button
                  fullWidth
                  variant={selectedMonth === i + 1 ? "contained" : "outlined"}
                  onClick={() => handleMonthClick(i + 1)}
                >
                  {i + 1}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {loading && <CircularProgress sx={{ mt: 2 }} />}
      {error   && <Alert severity="error">{error}</Alert>}

      {/* 🔥 COLLAPSE ALL / EXPAND ALL */}
      {Object.keys(tableData).length > 0 && (
        <Box display="flex" gap={1} mt={2}>
          <Button size="small" variant="outlined"
            onClick={() => setCollapsed(Object.fromEntries(Object.keys(tableData).map((k) => [k, false])))}>
            Expand All
          </Button>
          <Button size="small" variant="outlined"
            onClick={() => setCollapsed(Object.fromEntries(Object.keys(tableData).map((k) => [k, true])))}>
            Collapse All
          </Button>
        </Box>
      )}

      {/* 🔥 TABLE — one collapsible block per location */}
      {Object.keys(tableData).map((loc) => {
        const isOpen = !collapsed[loc]; // default open
        return (
          <Paper sx={{ mt: 2, overflow: "hidden" }} key={loc}>

            {/* ── Location Header (click to toggle) ── */}
            <Box
              onClick={() => toggleLocation(loc)}
              sx={{
                px: 2, py: 1.5, cursor: "pointer",
                bgcolor: "#f0f4ff",
                borderBottom: isOpen ? "1px solid #e0e0e0" : "none",
                display: "flex", alignItems: "center", justifyContent: "space-between"
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontWeight={700}>📍 {loc}</Typography>
                <Chip
                  size="small"
                  label={`${tableData[loc].length} bikes`}
                  sx={{ bgcolor: "#1565c0", color: "#fff", fontWeight: 700, fontSize: "0.7rem" }}
                />
              </Box>
              <IconButton size="small">
                {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Box>

            {/* ── Collapsible Table ── */}
            <Collapse in={isOpen}>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#fafafa" }}>
                      <TableCell sx={{ fontWeight: 700 }}>Model</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Engine</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Chasis</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData[loc].map((b) => (
                      <TableRow key={b._id} sx={{ "&:hover": { bgcolor: "#f5f8ff" } }}>
                        <TableCell>{b.model}</TableCell>
                        <TableCell>{b.engine}</TableCell>
                        <TableCell>{b.chasis}</TableCell>
                        <TableCell>₹{b.purchasePrice}</TableCell>
                        <TableCell>{new Date(b.date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button variant="contained" size="small" onClick={() => handleView(b._id)}>
                            VIEW
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>

          </Paper>
        );
      })}

      {/* 🔥 VIEW DIALOG — unchanged */}
      <Dialog open={openView} onClose={() => setOpenView(false)} fullWidth maxWidth="md">
        <Box p={3} sx={{ background: "#f8fafc" }}>
          <Typography variant="h6" fontWeight={700} mb={2}>Bike Full Details</Typography>

          {!viewData ? (
            <Alert severity="info">No data found</Alert>
          ) : (
            <Grid container spacing={2}>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography fontWeight={700} mb={1}>🚗 Bike Info</Typography>
                  <Typography><b>Model:</b> {viewData?.stockDetails?.model || "-"}</Typography>
                  <Typography><b>Engine:</b> {viewData?.stockDetails?.engine || "-"}</Typography>
                  <Typography><b>Chasis:</b> {viewData?.stockDetails?.chasis || "-"}</Typography>
                  <Typography><b>Colour:</b> {viewData?.stockDetails?.colour || "-"}</Typography>
                  <Typography><b>SKU:</b> {viewData?.stockDetails?.skuCode || "-"}</Typography>
                  <Typography><b>Key No:</b> {viewData?.stockDetails?.keyNumber || "-"}</Typography>
                  <Typography><b>Purchase Price:</b> ₹{viewData?.stockDetails?.purchasePrice || 0}</Typography>
                  <Typography><b>Ex-Showroom:</b> ₹{viewData?.stockDetails?.exShowroomPrice || 0}</Typography>
                  <Typography><b>Location:</b> {viewData?.stockDetails?.location || "-"}</Typography>
                  <Typography><b>Status:</b> {viewData?.currentStatus || "-"}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography fontWeight={700} mb={1}>👤 Created By</Typography>
                  <Typography><b>Name:</b> {viewData?.stockDetails?.createdBy?.name || "-"}</Typography>
                  <Typography><b>Email:</b> {viewData?.stockDetails?.createdBy?.email || "-"}</Typography>
                  <Typography><b>Date:</b> {viewData?.stockDetails?.date ? new Date(viewData.stockDetails.date).toLocaleString() : "-"}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography fontWeight={700} mb={1}>🔁 Transfer History</Typography>
                  {viewData?.transferHistory?.length > 0 ? (
                    viewData.transferHistory.map((t, i) => (
                      <Box key={i} sx={{ mb: 1, p: 1, borderBottom: "1px solid #eee" }}>
                        <Typography><b>{t?.fromLocation || "-"}</b> ➝ <b>{t?.toLocation || "-"}</b></Typography>
                        <Typography>By: {t?.transferredBy?.name || "-"}</Typography>
                        <Typography>Date: {t?.date ? new Date(t.date).toLocaleString() : "-"}</Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography>No transfer history</Typography>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 2, borderRadius: 2 }}>
                  <Typography fontWeight={700} mb={1}>💰 Sale Info</Typography>
                  {viewData?.saleDetails ? (
                    <>
                      <Typography><b>Customer:</b> {viewData.saleDetails.customerName || "-"}</Typography>
                      <Typography><b>Price:</b> ₹{viewData.saleDetails.salePrice || 0}</Typography>
                      <Typography><b>Type:</b> {viewData.saleDetails.saleType || "-"}</Typography>
                    </>
                  ) : (
                    <Typography>Bike not sold yet</Typography>
                  )}
                </Paper>
              </Grid>

            </Grid>
          )}
        </Box>
      </Dialog>

    </Box>
  );
};

export default EntryReport;